import React from "react";
import {
  KeyValue,
  KeyValueObject,
  KeyNumberValue,
  ResultRecord,
  TableHeader,
  SucceedingInstitution,
  SearchHighlightResults,
  KeyStringValue,
  ZipFile,
} from "../../interfaces";
import {
  ACQ_ADDRESS,
  ACQ_MAILING_ADDRESS,
  FROM_ADDRESS,
  FROM_OFFICE_ADDRESS,
  MAILING_ADDRESS,
  OFFICE_ADDRESS,
  OUT_ADDRESS,
  PHYSICAL_ADDRESS,
  SUR_ADDRESS,
  SUR_MAILING_ADDRESS,
  TRADE_NAMES,
  PRIOR_NAMES,
  WEBSITES,
  DEFAULT_ADDRESS,
  BRANCH_ADDRESS,
} from "../../refdata/constants";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Typography } from "@mui/material";
/**
 * Returns the first matching object based on key and value.
 * Undefined is returned if there are no matching objects
 */
export function getObjectFromArray(
  arr: KeyValue[],
  key: string,
  matchingValue: any,
) {
  return arr.filter((data: KeyValue) => {
    return data[key] === matchingValue;
  })[0];
}

export function sortObjKeys(o: KeyValue) {
  return Object.keys(o)
    .sort()
    .reduce((Obj: KeyValue, key: any) => {
      Obj[key] = o[key];
      return Obj;
    }, {});
}

export function sortObjValue(o: KeyValue[], key: string, desc?: boolean) {
  return o.sort((a: KeyValue, b: KeyValue) => {
    const val1 = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
    const val2 = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];

    if (val1 < val2) {
      return desc ? 1 : -1;
    }
    if (val1 > val2) {
      return desc ? -1 : 1;
    }
    return 0;
  });
}

export function toTitleCase(str: string | undefined) {
  return str
    ? str.replace(/\b[\w']+\b/g, (txt: string) => {
        if (txt.toLowerCase() === "fdic") {
          return "FDIC";
        } else if (txt.toLowerCase() === "bhc") {
          return "BHC";
        } else if (txt.toLowerCase() === "bhcs") {
          return "BHCs";
        }
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      })
    : "";
}

export function createUrlSafePathing(str: string) {
  return str.replace(/ /g, "_").replace(/&/g, "-").toLowerCase();
}

export function chunkArray<T>(array: T[], size: number): T[][] {
  if (!Array.isArray(array) || size <= 0) return [];

  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export function formatAddress(
  address: string | undefined,
  city: string | undefined,
  state: string | undefined,
  zip: string | undefined,
) {
  // TODO: Define a concrete default returned value
  return address && city && state
    ? `${toTitleCase(address)}, ${toTitleCase(
        city,
      )}, ${state.toUpperCase()} ${zip}`
    : "";
}

export function formatDate(data: any, longDate?: boolean): string {
  const formattedDate = new Date(data).toLocaleDateString("en-US", {
    month: longDate ? "long" : "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  // TODO: Define a concrete default returned value
  return data ? formattedDate : "";
}

export function formatRepDate(repDate: any) {
  return repDate && !repDate.toString().includes("/")
    ? `${repDate.toString().substring(4, 6)}/${repDate
        .toString()
        .substring(6, 8)}/${repDate.toString().substring(0, 4)}`
    : repDate;
}

export function fileNameFormat(fileName: string, cert?: string, name?: string) {
  return cert && name
    ? `${name}_${cert}_${fileName}_${new Date().toLocaleDateString("en-US")}`
        .replace(/&/g, "And")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/,/g, "")
    : `${fileName}_${new Date().toLocaleDateString("en-US")}`
        .replace(/[ /]/g, "_")
        .replace(/,/g, "");
}
export function SODCustomfileNameFormat(fileName: string) {
  return `${fileName}_06_30`.replace(/[ /]/g, "_").replace(/,/g, "");
}
export function applyMissingFields<T>(template: T, data: T) {
  return { ...template, ...data };
}

export function createHtmlId(str: string | undefined) {
  return str ? str.toLowerCase().replace(/\s/g, "-") : "";
}

export function arraySum(input: KeyNumberValue[]) {
  const sumObject: KeyNumberValue = {};

  input.forEach((data: KeyNumberValue) => {
    Object.keys(data).forEach((key) => {
      if (!sumObject.hasOwnProperty(key)) {
        sumObject[key] = data[key];
      } else {
        sumObject[key] += data[key];
      }
    });
  });
  return sumObject;
}

export function getExpandState(
  expandState: boolean | undefined,
  defaultState: boolean,
) {
  return expandState !== undefined ? expandState : defaultState;
}

export function addressLookup(data: KeyValue): KeyValue {
  return {
    [DEFAULT_ADDRESS]: {
      address: data.ADDRESS,
      address2: data.ADDRESS2,
      city: data.CITY,
      state: data.STALP,
      zip: data.ZIP,
    },
    [BRANCH_ADDRESS]: {
      address: data.ADDRESBR,
      city: data.CITY2BR,
      state: data.STALPBR,
      zip: data.ZIPBR,
    },
    [PHYSICAL_ADDRESS]: {
      address: data.PADDR,
      address2: data.PADDR2,
      city: data.PCITY,
      state: data.PSTALP,
      zip: data.PZIP5,
    },
    [OFFICE_ADDRESS]: {
      address: data.OFF_PADDR,
      address2: data.OFF_PADDR2,
      city: data.OFF_PCITY,
      state: data.OFF_PSTALP,
      zip: data.OFF_PZIP5,
    },
    [OUT_ADDRESS]: {
      address: data.OUT_PADDR,
      address2: data.OUT_PADDR2,
      city: data.OUT_PCITY,
      state: data.OUT_PSTALP,
      zip: data.OUT_PZIP5,
    },
    [FROM_ADDRESS]: {
      address: data.FRM_PADDR,
      address2: data.FRM_PADDR2,
      city: data.FRM_PCITY,
      state: data.FRM_PSTALP,
      zip: data.FRM_PZIP5,
    },
    [FROM_OFFICE_ADDRESS]: {
      address: data.FRM_OFF_PADDR,
      address2: data.FRM_OFF_PADDR2,
      city: data.FRM_OFF_PCITY,
      state: data.FRM_OFF_PSTALP,
      zip: data.FRM_OFF_PZIP5,
    },
    [MAILING_ADDRESS]: {
      address: data.MADDR,
      address2: data.MADDR2,
      city: data.MCITY,
      state: data.MSTALP,
      zip: data.MZIP5,
    },
    [SUR_ADDRESS]: {
      address: data.SUR_PADDR,
      address2: data.SUR_PADDR2,
      city: data.SUR_PCITY,
      state: data.SUR_PSTALP,
      zip: data.SUR_PZIP5,
    },
    [SUR_MAILING_ADDRESS]: {
      address: data.SUR_MADDR,
      address2: data.SUR_MADDR2,
      city: data.SUR_MCITY,
      state: data.SUR_MSTALP,
      zip: data.SUR_MZIP5_RAW,
    },
    [ACQ_ADDRESS]: {
      address: data.ACQ_PADDR,
      address2: data.ACQ_PADDR2,
      city: data.ACQ_PCITY,
      state: data.ACQ_PSTALP,
      zip: data.ACQ_PZIP5_RAW,
    },
    [ACQ_MAILING_ADDRESS]: {
      address: data.ACQ_MADDR,
      address2: data.ACQ_MADDR2,
      city: data.ACQ_MCITY,
      state: data.ACQ_MSTALP,
      zip: data.ACQ_MZIP5_RAW,
    },
  };
}

export function isDefinedAndTrue(value: string | number) {
  return (
    value &&
    value !== "0" &&
    value !== 0 &&
    value.toString().toUpperCase() !== "NULL"
  );
}

export function bankClassLookup(input: string) {
  const lookup: { [key: string]: string } = {
    N: "National Banks, member of the Federal Reserve Systems (FRS)",
    SM: "State Chartered Banks, member of the Federal Reserve System (FRS)",
    NM: "State Chartered Banks, not member of the Federal Reserve System (FRS)",
    OI: "Insured Branches of Foreign Banks",
    SI: "FDIC Supervised State Savings Banks",
    SB: "Federal Savings Bank",
    SL: "Federal & State Savings and Loans",
    NC: "Non-FDIC-Insured Commercial Banks",
    NS: "Non-FDIC-Insured Thrifts",
    SA: "Non-FDIC-Insured Thrifts",
  };
  return lookup[input];
}
export const sortAlphaNum = (a: any, b: any) =>
  a.localeCompare(b, "en", { numeric: true });

export function setInstitutionStatus(
  institution: ResultRecord,
  pendingClosed: boolean = false,
) {
  openAndInsuredScenario(institution);
  closedWithGovAssistanceScenario(institution);
  closedWithoutGovAssistanceScenario(institution);
  relinquishedInsuranceScenario(institution);
  if (pendingClosed) {
    institution.ACTIVE = 0;
  }

  return institution;
}

export function openAndInsuredScenario(institution: ResultRecord) {
  if (institution.ACTIVE && !isBankClassUninsured(institution.BKCLASS)) {
    institution.INSTITUTION_STATUS = "OPEN";
  }
}

export function closedWithGovAssistanceScenario(institution: ResultRecord) {
  if (!institution.ACTIVE && !isBankClassUninsured(institution.BKCLASS)) {
    if ([211, 212, 213, 215, 216, 217].includes(institution.CHANGEC1)) {
      institution.INSTITUTION_STATUS = "CLOSED_WITH_GOV";
    } else if ([230, 231, 235].includes(institution.CHANGEC1)) {
      institution.INSTITUTION_STATUS = "CLOSED_WITH_GOV_MERGED";
    }
  }
}

export function closedWithoutGovAssistanceScenario(institution: ResultRecord) {
  if (!institution.ACTIVE && !isBankClassUninsured(institution.BKCLASS)) {
    if ([240].includes(institution.CHANGEC1)) {
      institution.INSTITUTION_STATUS = "CLOSED_WITHOUT_GOV";
    } else if ([221, 222, 223, 224].includes(institution.CHANGEC1)) {
      institution.INSTITUTION_STATUS = "CLOSED_WITHOUT_GOV_ACQUIRED_MERGE";
    }
  }
}

export function relinquishedInsuranceScenario(institution: ResultRecord) {
  if (
    institution.INSAGNT1 === "NONE" &&
    isBankClassUninsured(institution.BKCLASS)
  ) {
    institution.INSTITUTION_STATUS = "RELINQUISHED";
  }
}

function isBankClassUninsured(bankClass: string) {
  return ["NC", "NS", "CU"].includes(bankClass);
}

export function getMatchingSearchResults(
  resultHighlight: KeyValue | undefined,
): SearchHighlightResults {
  if (!resultHighlight) {
    return {
      tradeNames: [],
      priorNames: [],
      websites: [],
      count: 0,
    };
  }

  const tradeNameMatches = findAndFlatten(TRADE_NAMES, resultHighlight);
  const priorNameMatches = findAndFlatten(PRIOR_NAMES, resultHighlight);
  const websiteMatches = findAndFlatten(WEBSITES, resultHighlight);

  return {
    tradeNames: tradeNameMatches,
    priorNames: priorNameMatches,
    websites: websiteMatches,
    count:
      tradeNameMatches.length + priorNameMatches.length + websiteMatches.length,
  };
}

function findAndFlatten(fields: string[], value: KeyValue): string[] {
  const flatArray = fields
    .filter((field: string): boolean => {
      return value.hasOwnProperty(field);
    })
    .map((field: string): string => {
      return value[field];
    });

  return ([] as string[]).concat(...flatArray).sort(sortAlphaNum);
}

export function getMaximumLengthOfHeaders(arr: TableHeader[][]) {
  return Math.max(...arr.map((o: TableHeader[]) => o.length));
}

export function consumerAssistanceLookup(
  institution: ResultRecord,
  succeedingInstitution: SucceedingInstitution,
) {
  const lookup: KeyValue = {
    FDIC: {
      link: "https://ask.fdic.gov/fdicinformationandsupportcenter/s/?language=en_US",
      label: "Complaints & Questions with Personal Information",
      internal: true,
    },
    OCC: {
      link: "http://www.helpwithmybank.gov",
      label: "HelpWithMyBank.gov",
    },
    FED: {
      link: "http://www.federalreserveconsumerhelp.gov",
      label: "Federal Reserve Consumer Help",
    },
    OTS: {
      link: "",
      label: "",
    },
    STATE: {
      link: "",
      label: "",
    },
  };

  function consumerAssistanceLookupValue() {
    const failedStatus =
      institution.INSTITUTION_STATUS === "CLOSED_WITH_GOV" ||
      institution.INSTITUTION_STATUS === "CLOSED_WITH_GOV_MERGED";

    const institutionRegulatorValues = {
      CLOSED_WITH_GOV: "FDIC",
      CLOSED_WITH_GOV_MERGED: "FDIC",
      CLOSED_WITHOUT_GOV: institution.REGAGNT,
      CLOSED_WITHOUT_GOV_ACQUIRED_MERGE: institution.REGAGNT,
      OPEN: institution.REGAGNT,
      RELINQUISHED: institution.REGAGNT,
    };

    return succeedingInstitution.display && !failedStatus
      ? succeedingInstitution.primaryRegulator
      : institutionRegulatorValues[institution.INSTITUTION_STATUS];
  }
  return lookup[consumerAssistanceLookupValue()];
}

export function getPrimaryRegulator(value: string) {
  const primaryRegulatorLookup: KeyStringValue = {
    FDIC: "Federal Deposit Insurance Corporation",
    OCC: "Comptroller of the Currency",
    FED: "Federal Reserve Board",
    OTS: "Office of Thrift Supervision",
    STATE: "State Regulated",
  };
  return primaryRegulatorLookup[value] ? primaryRegulatorLookup[value] : value;
}

export function zipFileDownload(downloads: ZipFile[], zipFilename: string) {
  const zip = new JSZip();
  downloads.forEach((file: ZipFile) => {
    const filename = `${file.name}.${file.extension ? file.extension : "csv"}`;
    const blob = fetch(file.url, {
      headers: { "x-rate-limit": "off" },
    }).then((url) => url.blob());
    if (filename) {
      zip.file(filename, blob, { binary: true });
    }
  });
  return zip.generateAsync({ type: "blob" }).then((content) => {
    saveAs(content, zipFilename);
  });
}
export function dataDownloadHeaders(
  headerValues: TableHeader[][],
  headerKeys: KeyValue,
) {
  // gets keys from object
  const apiHeaderArray = Object.keys(headerKeys);

  return apiHeaderArray.length > 0
    ? headerValues.map((data) => {
        const downloadHeaders = data.map((header: KeyValue, index: number) => {
          const headerObj = { label: "", key: "" };
          header.toolTip || header.title === "Bank Classes"
            ? (headerObj.label = header.toolTip
                ? `${header.title}: ${header.toolTip}`
                : "Event Types")
            : (headerObj.label = "");
          headerObj.key = `${apiHeaderArray[index]}.value`;

          return headerObj;
        });

        // filters out empty header values so data aligns properly to header
        return downloadHeaders.filter((value: KeyStringValue) => {
          return value.label !== "" ? value.label : "";
        });
      })
    : [""];
}

export function yearChoice(
  startYear: number,
  endYear: number,
  monthDay?: string,
) {
  const years = [];
  const monthAndDay = monthDay ? monthDay : "";
  for (let i = startYear; i <= endYear; i++) {
    years.push({ label: `${monthAndDay}${i}`, value: `${i}` });
  }
  return years;
}

export function appTitles(app: string) {
  const lookup: KeyValue = {
    homePage: "BankFind Suite",
    bankFind: "Find Insured Banks",
    bankFindDetails: "Institution Details",
    api: "API Documentation",
    oscr: "Bank Structure Changes",
    oscrDetails: "Events & Changes Details",
    historical: "Annual Data Summaries",
    failures: "Failures and Assistance",
    financials: "Reports and Comparisons",
    financialsDetails: "Institution Financial Reports",
    financialReport: "Financial/Regulatory Search & Reporting",
    peerGroup: "Standard Comparisons",
    standardPeerGroup: "Standard Comparisons",
    customizedPeerGroup: "Customized Comparisons",
    manageSavedPeerGroups: "Manage Your Saved Custom Content",
    help: "Help Library",
    SOD: "Summary of Deposits",
    branchOffice: "Summary of Deposits—Branch Office Deposits",
    summaryTables: "Summary of Deposits—Summary Tables",
    marketShare: "Summary of Deposits—Market Share Reports",
    customDataDownload: "Summary of Deposits—Custom Data Download",
    bulkData: "Bulk Data and API",
    api101: "API 101",
    bulkDataDownload: "Bulk Data Download",
  };
  return lookup[app];
}

export function removeUrlParameter(url: string, paramKey: string[]) {
  const updatedUrl = new URL(url);
  paramKey.forEach((param: string) => {
    updatedUrl.searchParams.delete(param);
  });
  return {
    search: updatedUrl.search,
  };
}

export function removeDuplicateArrayObj(arrayObj: KeyValue[], key: string) {
  return arrayObj.filter(
    (value, index, self) =>
      index === self.findIndex((duplicate) => duplicate[key] === value[key]),
  );
}

export function appHeaderTitle(appName: string, beta?: boolean) {
  return (
    <Typography variant="h1" id={`${appName}-title`}>
      <span style={{ fontWeight: "bold" }}>BankFind Suite:</span>{" "}
      {appTitles(appName)}
      {beta ? (
        <sup
          style={{
            display: "inline-block",
            fontSize: 24,
            color: "#AA0033",
          }}
        >
          BETA
        </sup>
      ) : (
        ""
      )}
    </Typography>
  );
}

export function reduceObj(arrayObj: KeyValueObject[]) {
  return arrayObj.reduce(
    (obj: KeyValue, item: KeyValue) =>
      Object.assign(obj, { [item.key]: item.value }),
    {},
  );
}

export function removeKeyPrefix(obj: KeyValue, prefix: string) {
  for (const keyName in obj) {
    if (keyName.startsWith(prefix)) {
      const replaced_key = keyName.replace(prefix, "");
      obj[replaced_key] = obj[keyName];
      delete obj[keyName];
    }
  }
  return obj;
}

export const serviceTypeLookup = (serviceNumber: number) => {
  const lookup: KeyValue = {
    11: "Full Service, Brick and Mortar Office",
    12: "Full Service, Retail Office",
    13: "Full Service, Home Banking",
    21: "Limited Service, Administrative Office",
    22: "Limited Service, Military Facility",
    23: "Limited Service, Drive-Through/ Facility Office",
    24: "Limited Service, Loan Production Office",
    25: "Limited Service, Consumer Credit Office",
    26: "Limited Service, Contractual Office",
    27: "Limited Service, Messenger Office",
    28: "Limited Service, Retail Office",
    29: "Limited Service, Mobile/Seasonal Office",
    30: "Limited Service, Trust Office",
  };
  return lookup[serviceNumber] ? lookup[serviceNumber] : serviceNumber;
};

export function displayPercentage(percent: number) {
  return Math.floor(Math.random() * 100) + 1 <= percent;
}
export function getCookiebyName(cookieName: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  let cookie: Record<string, string> = {};
  document.cookie.split(";").forEach((el) => {
    const [key, value] = el.replace(/ /g, "").split("=");
    cookie[key] = value;
  });
  return cookie[cookieName];
}
export function deleteCookiebyName(name: string) {
  return (document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`);
}
export function sumValueArrayObj(data: KeyValue[], prop: string) {
  return data.reduce((sum: number, data: KeyValue) => sum + data[prop], 0);
}
export const sodVariableDefinitionData = [
  {
    id: "mandatory",
    title: "Mandatory Variables",
    children: [
      {
        id: "YEAR",
        variable: "YEAR",
        title: "Report Year (Institution)",
        definition:
          "Year of survey (1994 - Current). All SOD surveys are annual as of June 30.",
        SODType: "Institution",
      },
      {
        id: "CERT",
        variable: "CERT",
        title: "FDIC Certificate #",
        definition:
          "A unique number assigned by the FDIC used to identify institutions and for the issuance of insurance certificates.",
        SODType: "Institution",
      },
      {
        id: "NAMEFULL",
        variable: "NAMEFULL",
        title: "Institution Name at Time of Reporting",
        definition: "Institution Name at Time of Reporting",
        SODType: "Institution",
      },
      {
        id: "ADDRESBR",
        variable: "ADDRESBR",
        title: "Address (Branch)",
        definition: "Physical location of the branch",
        SODType: "Branch",
      },
      {
        id: "BRNUM",
        variable: "BRNUM",
        title: "Branch Office Number (Branch)",
        definition:
          "Numerical reference to identify a branch office within one institution",
        SODType: "Branch",
      },
      {
        id: "UNINUMBR",
        variable: "UNINUMBR",
        title: "Unique Identification Number for a Branch Office (Branch)",
        definition:
          "The unique number associated with a specific physical location of a branch. This number remains with this location over time, regardless of ownership.",
        SODType: "Branch",
      },
      {
        id: "STALPBR",
        variable: "STALPBR",
        title: "State Code (Branch)",
        definition:
          "The state abbreviation of the location in which the branch is physically located.",
        SODType: "Branch",
      },
    ],
  },
  {
    id: "institution",
    title: "Institutions",
    children: [
      {
        id: "ADDRESS",
        variable: "ADDRESS",
        title: "Street Address",
        definition:
          "The street address at which an institution or branch office is physically located.",
        SODType: "Institution",
      },
      {
        id: "ASSET",
        variable: "ASSET",
        title: "Total Assets",
        definition:
          "( YTD, $ ) The sum of all assets owned by the institution including cash, loans, securities, bank premises and other assets. This total does not include off-balance-sheet accounts.",
        SODType: "Institution",
      },
      {
        id: "BKCLASS",
        variable: "BKCLASS",
        title: "Institution Class",
        definition:
          "A classification code assigned by the FDIC based on the institution's charter type (commercial bank or savings institution), charter agent (state or federal), Federal Reserve membership status (Fed member, Fed non-member) and its primary federal regulator (state chartered institutions are subject to both federal and state supervision). N - Commercial bank, national (federal) charter, Fed member, and supervised by the Office of the Comptroller of the Currency (OCC);  NM - Commercial bank, state charter, Fed non-member, and supervised by the Federal Deposit Insurance Corporation (FDIC);  OI - Insured U.S. branch of a foreign chartered institution (IBA) and supervised by the OCC or FDIC;  SB Federal savings banks, federal charter, supervised by the OCC or before July 21,2011 the Office of Thrift Supervision (OTS);  SI - State chartered stock savings banks, supervised by the FDIC;  SL - State chartered stock savings and loan associations, supervised by the FDIC or before July 21,2011 the OTS;  SM - Commercial bank, state charter, Fed member, and supervised by the Federal Reserve Bank (FRB);  NC - Non-insured non-deposit commercial banks and/or trust companies regulated by the OCC, a state, or a territory;  NS - Non-insured stock savings bank supervised by a state or territory;  CU - state or federally chartered credit unions supervised by the National Credit Union Association (NCUA). Note: The BKCLASS value represents the institution’s class as of the SOD survey date.",
        SODType: "Institution",
      },
      {
        id: "CALL",
        variable: "CALL",
        title: "CALL and TFR Report identifier (Institution)",
        definition:
          "Prior to 2012, OTS institutions filed a quarterly Thrift Financial Report (TFR). All other institutions file the CALL report.",
        SODType: "Institution",
      },
      {
        id: "CHARTER",
        variable: "CHARTER",
        title: "Charter Type (Institution)",
        definition:
          "Identifies whether an institution is federally or state chartered.",
        SODType: "Institution",
      },
      {
        id: "CHRTAGNN",
        variable: "CHRTAGNN",
        title: "Charter Agent Name (Institution)",
        definition:
          "The name of the chartering agency of the institution: OCC = COMPTROLLER OF THE CURRENCY OTS = OFFICE OF THRIFT SUPERVISION STATE = STATE AGENCY SOVER = FOREIGN COUNTRY",
        SODType: "Institution",
      },
      {
        id: "CHRTAGNT",
        variable: "CHRTAGNT",
        title: "Chartering Agency",
        definition:
          "The abbreviation of the chartering agency of the institution: OCC = COMPTROLLER OF THE CURRENCY OTS = OFFICE OF THRIFT SUPERVISION STATE = STATE AGENCY SOVER = FOREIGN COUNTRY",
        SODType: "Institution",
      },
      {
        id: "CITY",
        variable: "CITY",
        title: "City",
        definition:
          "City in which an Institution's headquarters or one of its branches is physically located. Either the entire name or part of the name of a specific city may be entered to produce an Institution List.",
        SODType: "Institution",
      },
      {
        id: "CLCODE",
        variable: "CLCODE",
        title: "Numeric Code for Bank Class",
        definition:
          "A number that sub-categorizes a major class of institutions. 3 = National bank, Federal Reserve System (FRS) member; 13 = State commercial bank, FRS member; 15 = State savings, co-op, or industrial bank, FRS member; 21 = State commercial bank, not FRS member; 23 = State savings, co-op, or industrial bank, not FRS member; 25 - State mutual commercial bank, not FRS member; 33 = Federal chartered stock savings bank; 34 = Federal chartered mutual saving bank; 35 = State chartered stock savings and loan association; 36 = State chartered mutual savings and loan association; 37 = Federal chartered stock savings and loan association; 38 = Federal chartered mutual savings and loan association; 41 = State chartered stock savings bank; 42 = State chartered mutual savings bank; 43 = Federal chartered stock savings bank (historical); 44 = Federal chartered mutual savings bank (historical); 50 = OCC chartered nondeposit and/or noninsured trust companies; 51 = Noninsured commercial bank; 52 = Noninsured domestic offices of foreign bank (International Banking Act); 53 = Noninsured industrial bank; 54 = State chartered nondeposit and/or noninsured trust company, not FRS member; 55 = State chartered domestic branches of foreign banks; 56 = OCC chartered domestic branches of foreign banks; 57 = New York investment company; 58 = State chartered nondeposit and/or noninsured trust company, FRS member; 59 = OTS chartered nondeposit and/or noninsured trust company; 61 = Noninsured private bank; 62 = Noninsured loan workout bank, OCC chartered; 63 = Noninsured loan workout bank, state chartered, FRS member; 64 = Noninsured loan workout bank, state chartered, not FRS member; 65 = Other holding company; 71 = Transfer agent; 81 = Noninsured stock savings bank; 82 = Noninsured mutual savings bank; 85 = Noninsured stock savings and loan association; 86 = Noninsured mutual savings and loan association; 89 = Noninsured insurance company; 91 = State chartered credit unions; 92 = Federal chartered credit unions; 93 = Privately insured state credit union.",
        SODType: "Institution",
      },
      {
        id: "CNTRYNA",
        variable: "CNTRYNA",
        title: "Country Name (Institution)",
        definition:
          "The country name where the headquarters of the institution is chartered.",
        SODType: "Institution",
      },
      {
        id: "DENOVO",
        variable: "DENOVO",
        title: "Denovo Institution Flag",
        definition:
          "A flag used to indicate whether an institution is a new institution (not a recharter). This flag is set quarterly. For instance, if REPDTE is 3/31/98 and DENOVO equals 1, the institution was a denovo during the first quarter of 1998.",
        SODType: "Institution",
      },
      {
        id: "DEPDOM",
        variable: "DEPDOM",
        title: "Deposits held in domestic offices",
        definition:
          "( YTD, $ ) The sum of all domestic office deposits (SOD data as of June 30), including demand deposits, money market deposits, other savings deposits and time deposits.",
        SODType: "Institution",
      },
      {
        id: "DEPSUM",
        variable: "DEPSUM",
        title: "Total Deposits (Institution)",
        definition:
          "Total domestic deposits of the institution as of June 30. This amount is only reported for the main office locations.",
        SODType: "Institution",
      },
      {
        id: "DOCKET",
        variable: "DOCKET",
        title: "OTS Docket Number",
        definition:
          "A unique identification number assigned to institutions chartered by the Office of Thrift Supervision (OTS) or members of the Federal Housing Finance Board (FHFB) and formerly by the Federal Home Loan Bank (FHLB) Board. The value is '00000' for institutions not members of the FHFB.",
        SODType: "Institution",
      },
      {
        id: "ESCROW",
        variable: "ESCROW",
        title: "Escrow Accounts (TFR) (Institution)",
        definition:
          "Escrow deposits report on the Thrift Financial Reports. These are included in the total domestic deposits on the Call Reports.",
        SODType: "Institution",
      },
      {
        id: "FDICDBS",
        variable: "FDICDBS",
        title: "FDIC Region Number",
        definition:
          "The FDIC Office assigned to the geographic area. The eight FDIC Regions and their respective states are: Boston - Connecticut, Maine, Massachusetts, New Hampshire, Rhode Island, Vermont New York - Delaware, District of Columbia, Maryland, New Jersey, New York, Pennsylvania, Puerto Rico, U.S. Virgin Islands Atlanta - Alabama, Florida, Georgia, North Carolina, South Carolina, Virginia, West Virginia Memphis - Arkansas, Kentucky, Louisiana, Mississippi, Tennessee Chicago - Illinois, Indiana, Michigan, Ohio, Wisconsin Kansas City - Iowa, Kansas, Minnesota, Missouri, Nebraska, North Dakota, South Dakota Dallas - Colorado, New Mexico, Oklahoma, Texas San Francisco - Alaska, American Samoa, Arizona, California, Guam, Hawaii, Idaho, Montana, Nevada, Oregon, States of Micronesia, Utah, Washington, Wyoming",
        SODType: "Institution",
      },
      {
        id: "FDICNAME",
        variable: "FDICNAME",
        title: "FDIC Region Name (Institution)",
        definition:
          "The name of the FDIC Regional Office that services the institution.",
        SODType: "Institution",
      },
      {
        id: "FED",
        variable: "FED",
        title: "Federal Reserve ID Number",
        definition:
          "A number used to identify the Federal Reserve district in which the institution is located. 01 - Boston, 02 - New York, 03 - Philadelphia, 04 - Cleveland, 05 - Richmond, 06 - Atlanta, 07 - Chicago, 08 - St. Louis, 09 - Minneapolis, 10 - Kansas city, 11 - Dallas, 12 - San Francisco",
        SODType: "Institution",
      },
      {
        id: "FEDNAME",
        variable: "FEDNAME",
        title: "Federal Reserve District Name (Institution)",
        definition:
          "The name that identifies the Federal Reserve District where the institution is located.",
        SODType: "Institution",
      },
      {
        id: "INSAGNT1",
        variable: "INSAGNT1",
        title: "Primary Insurance Agency/Fund",
        definition:
          "The primary insurer, insurance agent, or insurance status of an institution BIF - BANK INSURANCE FUND DIF - DEPOSIT INSURANCE FUND SAIF - SAVINGS INSURANCE FUND STATE - STATE INSURED PRIV - PRIVATE INSURED SOVER - FOREIGN INSURED OTHER - OTHER INSURED NONE - NOT INSURED NCUA - NATIONAL CREDIT UNION ASSOCIATION INSURANCE",
        SODType: "Institution",
      },
      {
        id: "INSBRDD",
        variable: "INSBRDD",
        title: "Demand Deposits in Insured Branches (Institution)",
        definition:
          "Demand deposits in insured branches located in Puerto Rico and U.S. Territories and Possessions.",
        SODType: "Institution",
      },
      {
        id: "INSBRTS",
        variable: "INSBRTS",
        title: "Time & Savings Deposits in Insured Branches (Institution)",
        definition:
          "Time and savings deposits in insured branches located in Puerto Rico and U.S. Territories and Possessions.",
        SODType: "Institution",
      },
      {
        id: "INSURED",
        variable: "INSURED",
        title: "INSURED (Institution)",
        definition:
          "Identifies commercial banks (CB), savings institutions (SA), and insured branches of foreign banks (IB)",
        SODType: "Institution",
      },
      {
        id: "OCCDIST",
        variable: "OCCDIST",
        title: "Office of The Comptroller District",
        definition:
          "The Office of the Comptroller of the Currency (OCC) District in which the institution is physically located. The eight OCC Districts and their respective regions are:01 - Northeastern District03 - Central District04 - Western District05 - Southern District06 - Large Bank Supervision Locations07 - Midsize Bank Supervision Locations08 - Special Supervision For more information go to OCC: https://www.occ.treas.gov/about/who-we-are/locations/index-locations.html",
        SODType: "Institution",
      },
      {
        id: "OCCNAME",
        variable: "OCCNAME",
        title: "OCC Region Name (Institution)",
        definition:
          "The name associated with the Comptroller of the Currency district in which the institution is located; 01-Northeast, 03 Central, 04 Western and 05 Southern.",
        SODType: "Institution",
      },
      {
        id: "REGAGNT",
        variable: "REGAGNT",
        title: "Primary Federal Regulator (Institution)",
        definition:
          "There are now three federal regulators of banks and savings and loan institutions. Before July 21, 2011, there were four federal regulators: federal deposit insurance corporation (FDIC) - primary federal regulator responsible for state-chartered banks not members of the Federal Reserve System, state chartered savings banks and as of July 21, 2011, state chartered thrifts. Federal Reserve Board (FRB) - Primary federal regulator responsible for state-chartered commercial and savings bank members of the Federal Reserve System. Office of the Comptroller of the Currency (OCC) - Primary federal regulator responsible for nationally chartered commercial banks, and as of July 21, 2011, federally chartered thrifts. Office of Thrift Supervision (OTS) - Prior to its merger into the OCC on July 21, 2011, primary federal regulator responsible for federally chartered savings and loan associations, federal savings banks and state-chartered savings and loan associations. FDIC-Insured depository institutions are members of the Deposit Insurance Fund (DIF).",
        SODType: "Institution",
      },
      {
        id: "RSSDID",
        variable: "RSSDID",
        title: "Federal Reserve ID Number",
        definition:
          "A unique number assigned by the Federal Reserve Board as the entity's unique identifier (RSSDID) for the Regulatory top holder.Note: information on bank holding companies is only as of quarter-end.regulatory top holder is any company that directly or indirectly owns, controls or has power to vote 25 percent or more of a bank's or direct holding company's shares or controls in any manner the election of a majority of the directors or trustees of a bank or direct holding company or exercises a controlling influence over the management or policies of a bank or direct holding company. Information on Thrift Holding Companies that own Savings Associations but do not own banks is not currently available in the ID System. Source: Federal Reserve Board National Information Center data base.",
        SODType: "Institution",
      },
      {
        id: "SPECDESC",
        variable: "SPECDESC",
        title: "Asset Concentration Hierarchy Description (Institution)",
        definition:
          "The description of a mutually exclusive industry classification grouping which indicates the institution’s primary asset specialization. . = NOT AVAILABLE 0 = NO SPECIALIZATION GROUP 1 = INTERNATIONAL SPECIALIZATION 2 = AGRICULTURAL SPECIALIZATION 3 = CREDIT-CARD SPECIALIZATION 4 = COMMERCIAL LENDING SPECIALIZATION 5 = MORTGAGE LENDING SPECIALIZATION 6 = CONSUMER LENDING SPECIALIZATION 7 = OTHER SPECIALIZED UNDER 1 BILLION 8 = ALL OTHER UNDER 1 BILLION 9 = ALL OTHER OVER 1 BILLION",
        SODType: "Institution",
      },
      {
        id: "SPECGRP",
        variable: "SPECGRP",
        title: "Asset Concentration Hierarchy",
        definition:
          "An indicator of an Institution’s primary specialization in terms of Asset Concentration.Note: (Groups are hierarchical and mutually exclusive):International Specialization – Institutions with assets greater than $10 billion and more than 25 percent of total assets in foreign offices.Agricultural Specialization – Banks with agricultural production loans plus real estate loans secured by farmland in excess of 25 percent of total loans and leases.Credit-Card Specialization – Institutions with credit-card loans plus securitized receivables in excess of 50 percent of total assets plus securitized receivables.Commercial Lending Specialization – Institutions with commercial and industrial loans, plus real estate construction and development loans, plus loans secured by commercial real estate properties in excess of 25 percent of total assets.Mortgage Lending Specialization – Institutions with residential mortgage loans, plus mortgage-backed securities, in excess of 50 percent of total assets.Consumer Lending Specialization – Institutions with residential mortgage loans, plus credit-card loans, plus other loans to Individuals, in excess of 50 percent of total assets.Other Specialized $1 Billion – Institutions with assets greater than $1 billion that do not meet any of the definitions above, they have significant lending activity with no identified asset concentrations.Note: Also see SPECDESC",
        SODType: "Institution",
      },
      {
        id: "STALP",
        variable: "STALP",
        title: "State Code",
        definition:
          "The State Abbreviation of the location of the Institution's main office.",
        SODType: "Institution",
      },
      {
        id: "STCNTY",
        variable: "STCNTY",
        title: "State and County Number",
        definition:
          "A five digit number representing the state and county in which the Institution is physically located. The first two digits represent the FIPS State numeric code and the last three digits represent the FIPS County numeric code.",
        SODType: "Institution",
      },
      {
        id: "STNAME",
        variable: "STNAME",
        title: "State Name",
        definition:
          "State in which the institution or branch is physically located. The FDIC Act defines state as any State of the United States, the District of Columbia, and any territory of the United States, Puerto Rico, Guam, American Samoa, the Trust Territory of the Pacific Islands, the Virgin Island, and the Northern Mariana Islands.",
        SODType: "Institution",
      },
      {
        id: "UNIT",
        variable: "UNIT",
        title: "Unit Bank flag (Institution)",
        definition:
          "A flag that identifies an institution with only a main office location; no branch locations.",
        SODType: "Institution",
      },
      {
        id: "ZIP",
        variable: "ZIP",
        title: "Zip Code",
        definition:
          "The ZIP code associated with the physical address of the institution's headquarters.",
        SODType: "Institution",
      },
    ],
  },
  {
    id: "branch",
    title: "Branch Office Variables",
    children: [
      {
        id: "BKMO",
        variable: "BKMO",
        title: "Main Office Designation Flag (Branch)",
        definition:
          "Main office / branch identifier; Main office = 1, branch = 0",
        SODType: "Branch",
      },
      {
        id: "BRCENM",
        variable: "BRCENM",
        title: "Branch Office CENCODES",
        definition:
          "Code used to describe the nature of the reported deposits: C - Combined E - Estimated N - Non Deposit M - Main Office",
        SODType: "Branch",
      },
      {
        id: "BRSERTYP",
        variable: "BRSERTYP",
        title: "Branch Service Type (Branch)",
        definition:
          "Code that defines the type of service the branch office provides: 11- Full Service, brick and mortar office 12- Full Service, retail office 13- Full Service, home banking 21- Limited Service, administrative office 22- Limited Service, military facility 23- Limited Service, drive-through/ facility office 24- Limited Service, loan production office 25- Limited Service, consumer credit office 26- Limited Service, contractual office 27- Limited Service, messenger office 28- Limited Service, retail office 29- Limited Service, mobile/seasonal office 30- Limited Service, trust office",
        SODType: "Branch",
      },
      {
        id: "CBSA_DIV_NAMB",
        variable: "CBSA_DIV_NAMB",
        title: "Metropolitan Division Name (Branch)",
        definition:
          "The name that corresponds to a code for a county or group of counties within a Core Based Statistical Area that contains a core with a population of at least 2.5 million. A Metropolitan Division consists of one or more main/secondary counties that represent an employment center or centers, plus adjacent counties associated with the main county or counties through commuting ties.",
        SODType: "Branch",
      },
      {
        id: "CITY2BR",
        variable: "CITY2BR",
        title: "City (USPS) (Branch)",
        definition: "Central city based on the reported ZIP code of the branch",
        SODType: "Branch",
      },
      {
        id: "CITYBR",
        variable: "CITYBR",
        title: "City (Reported)(Branch)",
        definition: "Reported city in which the branch is located",
        SODType: "Branch",
      },
      {
        id: "CNTRYNAB",
        variable: "CNTRYNAB",
        title: "FIPS Country Name (Branch)",
        definition: "Country name in which the branch is located.",
        SODType: "Branch",
      },
      {
        id: "CNTYNAMB",
        variable: "CNTYNAMB",
        title: "County Name (Branch)",
        definition: "County name in which the branch is located.",
        SODType: "Branch",
      },
      {
        id: "CNTYNUMB",
        variable: "CNTYNUMB",
        title: "County Number (Branch)",
        definition:
          "County number that corresponds to the county in which the branch is located.",
        SODType: "Branch",
      },
      {
        id: "CONSOLD",
        variable: "CONSOLD",
        title: "Consolidated number (Branch)",
        definition:
          "When BRCENM = C, consolidated, this is the office number (BRNUM) in which the deposits are reported. Both branches should be within the same state and county.",
        SODType: "Branch",
      },
      {
        id: "CSABR",
        variable: "CSABR",
        title: "Combined Statistical Areas (Branch)",
        definition:
          "The identifying code of a geographic entity consisting of two or more adjacent Core Based Statistical Areas (CBSAs) with employment interchange measures of at least 15. Pairs of CBSAs with employment interchange measures of at least 25 combine automatically. Pairs of CBSAs with employment interchange measures of at least 15, but less than 25, may combine if local opinion in both areas favors combination.",
        SODType: "Branch",
      },
      {
        id: "CSANAMBR",
        variable: "CSANAMBR",
        title: "Combined Statistical Area Name (Branch)",
        definition:
          "The name of a geographic entity consisting of two or more adjacent Core Based Statistical Areas (CBSAs) with employment interchange measures of at least 15. Pairs of CBSAs with employment interchange measures of at least 25 combine automatically. Pairs of CBSAs with employment interchange measures of at least 15, but less than 25, may combine if local opinion in both areas favors combination.",
        SODType: "Branch",
      },
      {
        id: "DEPSUMBR",
        variable: "DEPSUMBR",
        title: "Deposits (Branch)(in thousands of dollars)",
        definition: "Branch office deposits as of June 30",
        SODType: "Branch",
      },
      {
        id: "DIVISIONB",
        variable: "DIVISIONB",
        title: "Metropolitan Divisions Flag (Branch)",
        definition:
          "A county or group of counties within a Core Based Statistical Area that contains a core with a population of at least 2.5 million. A Metropolitan Division consists of one or more main/secondary counties that represent an employment center or centers, plus adjacent counties associated with the main county or counties through commuting ties.",
        SODType: "Branch",
      },
      {
        id: "METROBR",
        variable: "METROBR",
        title: "Metropolitan Division Flag (Branch)",
        definition:
          "An indicator for an area that contains a core urban area of 50,000 or more population",
        SODType: "Branch",
      },
      {
        id: "MICROBR",
        variable: "MICROBR",
        title: "Micropolitan Division Flag (Branch)",
        definition:
          "An indicator for an area contains an urban core of at least 10,000 (but less than 50,000) population.",
        SODType: "Branch",
      },
      {
        id: "MSABR",
        variable: "MSABR",
        title: "Metropolitan Statistical Areas (Branch)",
        definition:
          "The Metropolitan Statistical Areas (MSABR) are based on the 2010 Census. These areas correspond to the state/county/CBSA relationships as defined by the Census Bureau. For the purpose of the Summary of Deposits, MSAs reflect only the CBSA codes for metropolitan areas (use MSABR for the Summary of Deposits).",
        SODType: "Branch",
      },
      {
        id: "MSANAMB",
        variable: "MSANAMB",
        title: "Metropolitan Statistical Area Name (Branch)",
        definition:
          "The Metropolitan Statistical Area name in which the branch is physically located. The Metropolitan Statistical Areas (MSABR) are based on the 2010 Census. These areas correspond to the state/county/CBSA relationships as defined by the Census Bureau. For the purpose of the Summary of Deposits, MSAs reflect only the CBSA codes for metropolitan areas (use MSABR for the Summary of Deposits).",
        SODType: "Branch",
      },
      {
        id: "NAMEBR",
        variable: "NAMEBR",
        title: "Branch Name (Branch)",
        definition: "Branch name.",
        SODType: "Branch",
      },
      {
        id: "NECNAMB",
        variable: "NECNAMB",
        title: "New England City and Town Area Names (Branch Office)",
        definition:
          "Name of the New England City Town Areas based on the PLACENUM.",
        SODType: "Branch",
      },
      {
        id: "NECTABR",
        variable: "NECTABR",
        title: "New England City and Town Areas (Branch Office)",
        definition:
          "Code of the New England City Town Areas based on the PLACENUM.",
        SODType: "Branch",
      },
      {
        id: "PLACENUM",
        variable: "PLACENUM",
        title: "Place Number (Branch)",
        definition:
          "The FIPS MCD (Minor Civil Division) code refers to a subsection of the U.S. Census county subdivision statistics. The county subdivision FIPS includes census data for county divisions, census subareas, minor civil divisions, unorganized territories, and incorporated areas. ",
        SODType: "Branch",
      },
      {
        id: "SIMS_ACQUIRED_DATE",
        variable: "SIMS_ACQUIRED_DATE",
        title: "Acquired Date (Branch Office)",
        definition:
          "The date that a branch was last acquired by another institution",
        SODType: "Branch",
      },
      {
        id: "SIMS_DESCRIPTION",
        variable: "SIMS_DESCRIPTION",
        title: "SIMS_DESCRIPTION",
        definition:
          "The quality of the match of the branch's physical location to a latitude and longitude with a given projection.",
        SODType: "Branch",
      },
      {
        id: "SIMS_ESTABLISHED_DATE",
        variable: "SIMS_ESTABLISHED_DATE",
        title: "Established Date (Branch Office)",
        definition: "The date that the branch location was established.",
        SODType: "Branch",
      },
      {
        id: "SIMS_LATITUDE",
        variable: "SIMS_LATITUDE",
        title: "Physical Latitude Coordinates (Branch Office)",
        definition: "The latitude of the branch's physical location.",
        SODType: "Branch",
      },
      {
        id: "SIMS_LONGITUDE",
        variable: "SIMS_LONGITUDE",
        title: "Physical Longitude Coordinates (Branch Office)",
        definition: "The longitude of the branch's physical location",
        SODType: "Branch",
      },
      {
        id: "SIMS_PROJECTION",
        variable: "SIMS_PROJECTION",
        title: "SIMS_PROJECTION",
        definition:
          "The method used to arrive at the latitude and longitude of the branch's physical location.",
        SODType: "Branch",
      },
      {
        id: "STCNTYBR",
        variable: "STCNTYBR",
        title: "State & County Number (Branch)",
        definition:
          "The state and county FIPS code associated with the specific branch location.",
        SODType: "Branch",
      },
      {
        id: "STNAMEBR",
        variable: "STNAMEBR",
        title: "State Name (Branch)",
        definition: "The state name where branch is physically located.",
        SODType: "Branch",
      },
      {
        id: "STNUMBR",
        variable: "STNUMBR",
        title: "State Number (Branch)",
        definition:
          "The FIPS number of the state in which the branch is physically located.",
        SODType: "Branch",
      },
      {
        id: "USA",
        variable: "USA",
        title: "Domestic Institution flag",
        definition:
          "A flag that identifies that the institution is headquartered in the United States.",
        SODType: "Branch",
      },
      {
        id: "ZIPBR",
        variable: "ZIPBR",
        title: "Zip Code (Branch)",
        definition:
          "The ZIP code associated with the physical address of the branch.",
        SODType: "Branch",
      },
    ],
  },
  {
    id: "BHC",
    title: "BHCs",
    children: [
      {
        id: "CITYHCR",
        variable: "CITYHCR",
        title: "City of High Holder",
        definition:
          "City in which the headquarters of the institution's regulatory high holder are physically located.",
        SODType: "BHC",
      },
      {
        id: "HCTMULT",
        variable: "HCTMULT",
        title: "Multi-Bank Holding Company",
        definition:
          "A flag used to indicate whether an institution is a member of a multi-bank holding company 0 = No, 1 = Yes Only in SOD - The code that identifies the type of holding company associated with the branch. The options are: MULT - Multi-bank holding company; ONE - one bank holding company; NONE - not a member of a bank holding company.",
        SODType: "BHC",
      },
      {
        id: "NAMEHCR",
        variable: "NAMEHCR",
        title: "Name of Bank Holding Company (Regulatory Top Holder)",
        definition:
          "Regulatory top holder is assigned by the Federal Reserve Board based on ownership and control percentages. Note: Information on bank holding companies is only as of quarter-end. Regulatory top holder is any company that directly or indirectly owns, controls or has power to vote 25 percent or more of a bank's or direct holding company's shares or controls in any manner the election of a majority of the directors or trustees of a bank or direct holding company or exercises a controlling influence over the management or policies of a bank or direct holding company. Information on Thrift Holding Companies that own Savings Associations but do not own banks is not currently available in the ID System. Source: Federal Reserve Board National Information Center data base. Note: Information on bank holding companies is only as of quarter-end.Regulatory top holder is any company that directly or indirectly owns, controls or has power to vote 25 percent or more of a bank's or direct holding company's shares or controls in any manner the election of a majority of the directors or trustees of a bank or direct holding company or exercises a controlling influence over the management or policies of a bank or direct holding company. Information on Thrift Holding Companies that own Savings Associations but do not own banks is not currently available in the ID System. Source: Federal Reserve Board National Information Center data base.",
        SODType: "BHC",
      },
      {
        id: "RSSDHCR",
        variable: "RSSDHCR",
        title: "RSSDID - High Regulatory Holder",
        definition:
          "The unique number assigned by the Federal Reserve Board to the Regulatory High Holding Company of the Institution.",
        SODType: "BHC",
      },
      {
        id: "STALPHCR",
        variable: "STALPHCR",
        title: "Regulatory Holding Company State Location ",
        definition:
          "State Location of the Regulatory High Holding Company (either direct or indirect owner).",
        SODType: "BHC",
      },
    ],
  },
];
